// H.5 — Admin Plans Page (platform-admin writes).
//
// Grid editor: rows = customers (with "All customers" baseline pseudo-row
// at top), columns = months for the selected year. Three editable values
// per cell: Revenue / Freight count / Margin.
//
// Forwarders see this page READ-ONLY (server enforces 403 on writes too).
// Customer-tier users get the route-guard redirect like the rest of the BI.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Target, ChevronLeft, ChevronRight, Copy, Save, Loader2, AlertCircle, Users,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  letterSpacing: ls,
});

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Hooks
const usePlans = (year) =>
  useQuery({
    queryKey: ["bi-plans", year],
    queryFn: () => axios.get(`${API}/bi/plans?year=${year}`).then((r) => r.data),
  });

const useCustomerMasters = () =>
  useQuery({
    queryKey: ["customer-masters"],
    queryFn: () => axios.get(`${API}/customer-masters`).then((r) => {
      // Endpoint returns {items, count} on the forwarder portal admin path.
      const d = r.data;
      return Array.isArray(d) ? d : (d?.items || []);
    }),
  });

const useBulkUpsert = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(`${API}/bi/plans/bulk`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bi-plans"] }),
  });
};

const _cellKey = (custId, month) => `${custId || "__all__"}__${month}`;

const _emptyCell = () => ({ planned_revenue: "", planned_freight_count: "", planned_margin: "" });

const _toNum = (v) => (v === "" || v == null ? null : Number(v));

const _customerLabel = (c) => c?.tenant_name || c?.name || c?.id || "(unnamed)";

const cellInput = (val, onChange, testid) => (
  <input
    type="number"
    value={val ?? ""}
    onChange={(e) => onChange(e.target.value)}
    style={{
      background: "transparent",
      border: "1px solid rgba(255,255,255,0.10)",
      color: "#f0f2f8",
      ...mono(10),
      padding: "3px 5px",
      borderRadius: 3,
      width: "100%",
      minWidth: 70,
    }}
    data-testid={testid}
  />
);

export default function AdminBiPlans() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "platform_admin";

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());

  const { data: plans = [], isLoading: plansLoading, error: plansError } = usePlans(year);
  const { data: customers = [] } = useCustomerMasters();

  // Seed local state from server plans whenever year + plans change.
  const [draft, setDraft] = useState({}); // { cellKey: {planned_revenue, planned_freight_count, planned_margin} }

  useEffect(() => {
    const seed = {};
    for (const p of plans) {
      const k = _cellKey(p.customer_tenant_id, p.month);
      seed[k] = {
        planned_revenue: p.planned_revenue ?? "",
        planned_freight_count: p.planned_freight_count ?? "",
        planned_margin: p.planned_margin ?? "",
        _id: p.id,
      };
    }
    setDraft(seed);
  }, [plans, year]);

  // Customer rows. "All customers" baseline at top.
  // Stash forwarder_tenant_id from the customer-masters response so the
  // bulk-upsert payload can include it explicitly — this lets platform_admin
  // users (who often have tenant_id=null) save plans without the server
  // returning 400 missing_forwarder_tenant_id.
  const fwdTenantId = useMemo(
    () => customers.find((c) => c.forwarder_tenant_id)?.forwarder_tenant_id || null,
    [customers],
  );

  const rows = useMemo(() => {
    return [
      { id: null, label: "All customers (baseline)", testid: "row-all-customers" },
      ...customers.map((c) => ({
        id: c.id,
        label: c.display_name || c.tenant_name || c.name || c.id,
        testid: `row-customer-${c.id}`,
        tenant_id: c.customer_tenant_id || c.tenant_id || c.id,
      })),
    ];
  }, [customers]);

  const set = (cellKey, field, value) => {
    setDraft((prev) => ({
      ...prev,
      [cellKey]: { ...(prev[cellKey] || _emptyCell()), [field]: value },
    }));
  };

  const bulk = useBulkUpsert();
  const onSave = async () => {
    const body = { plans: [] };
    for (const row of rows) {
      for (let m = 1; m <= 12; m++) {
        const k = _cellKey(row.tenant_id ?? row.id, m);
        const c = draft[k];
        if (!c) continue;
        const r = _toNum(c.planned_revenue);
        const f = _toNum(c.planned_freight_count);
        const ma = _toNum(c.planned_margin);
        if (r == null && f == null && ma == null) continue;
        const plan = {
          customer_tenant_id: row.tenant_id ?? row.id,
          ...(fwdTenantId ? { forwarder_tenant_id: fwdTenantId } : {}),
          year, month: m,
          planned_revenue: r,
          planned_freight_count: f,
          planned_margin: ma,
        };
        body.plans.push(plan);
      }
    }
    if (body.plans.length === 0) {
      toast.error("Nothing to save");
      return;
    }
    try {
      const res = await bulk.mutateAsync(body);
      toast.success(`Saved · ${res.inserted} new · ${res.updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Save failed");
    }
  };

  const onCopyPrevYear = async () => {
    if (!window.confirm(`Copy ALL plans from ${year - 1} into ${year}? Existing rows will be overwritten.`)) return;
    try {
      const r = await axios.get(`${API}/bi/plans?year=${year - 1}`);
      const prev = r.data || [];
      if (prev.length === 0) {
        toast.error(`No plans found for ${year - 1}`);
        return;
      }
      const body = {
        plans: prev.map((p) => ({
          customer_tenant_id: p.customer_tenant_id,
          ...(fwdTenantId ? { forwarder_tenant_id: fwdTenantId } : {}),
          year, month: p.month,
          planned_revenue: p.planned_revenue,
          planned_freight_count: p.planned_freight_count,
          planned_margin: p.planned_margin,
        })),
      };
      const res = await bulk.mutateAsync(body);
      toast.success(`Copied ${prev.length} rows from ${year - 1} (${res.inserted} new, ${res.updated} updated)`);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  if (plansError?.response?.status === 403) {
    return (
      <div data-testid="bi-plans-403" className="rounded-lg p-6" style={{ background: "rgba(220,53,69,0.06)", border: "1px solid rgba(220,53,69,0.30)" }}>
        <AlertCircle size={16} color="#ef4444" className="inline mr-2" />
        <span style={mono(12, "#ef4444", 700)}>BI plans is forwarder-internal only. Your account doesn't have access.</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-bi-plans-page">
      <header className="mb-3">
        <h1 style={syne("clamp(22px, 3vw, 28px)", 800, "-0.5px")} data-testid="view-title">
          <Target size={20} className="inline mr-2" />
          BI · Plans (per customer × month)
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          {isPlatformAdmin ? "Platform-admin · enter planned revenue / freight count / margin per customer per month" : "Forwarder read-only view — contact your platform admin to edit"}
        </div>
      </header>

      <div className="flex items-center gap-2 mb-3" data-testid="bi-plans-toolbar">
        <button onClick={() => setYear((y) => y - 1)} className="px-2.5 py-1.5 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", cursor: "pointer" }} data-testid="bi-plans-year-prev"><ChevronLeft size={14} /></button>
        <span style={{ ...syne(18, 800, "-0.3px"), color: "#9ec3ec" }} data-testid="bi-plans-year-label">{year}</span>
        <button onClick={() => setYear((y) => y + 1)} className="px-2.5 py-1.5 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", cursor: "pointer" }} data-testid="bi-plans-year-next"><ChevronRight size={14} /></button>
        {isPlatformAdmin && (
          <>
            <button onClick={onCopyPrevYear} className="px-2.5 py-1.5 rounded inline-flex items-center gap-1 ml-2" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", ...mono(10, undefined, 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid="bi-plans-copy-prev"><Copy size={11} /> Copy from {year - 1}</button>
            <button onClick={onSave} className="px-3 py-1.5 rounded inline-flex items-center gap-1 ml-auto" style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase", opacity: bulk.isPending ? 0.7 : 1 }} disabled={bulk.isPending} data-testid="bi-plans-save"><Save size={11} /> {bulk.isPending ? "Saving…" : "Save changes"}</button>
          </>
        )}
      </div>

      {plansLoading && <div style={mono(11, "#94a3b8")}><Loader2 size={12} className="inline animate-spin mr-2" />Loading plans…</div>}

      <div className="overflow-x-auto rounded-lg" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-plans-grid">
        <table style={{ width: "100%", borderCollapse: "collapse", ...mono(10) }}>
          <thead>
            <tr style={{ background: "rgba(0,0,0,0.18)" }}>
              <th style={{ ...mono(9, "#94a3b8", 700, "0.5px"), padding: "8px 8px", textAlign: "left", textTransform: "uppercase", position: "sticky", left: 0, background: "rgba(0,0,0,0.30)" }}>Customer</th>
              {MONTHS.map((m, i) => (
                <th key={i} style={{ ...mono(9, "#94a3b8", 700, "0.5px"), padding: "8px 8px", textAlign: "center", textTransform: "uppercase", minWidth: 220 }} colSpan={3}>{m}</th>
              ))}
            </tr>
            <tr style={{ background: "rgba(0,0,0,0.10)" }}>
              <th style={{ position: "sticky", left: 0, background: "rgba(0,0,0,0.30)", padding: "5px 8px" }} />
              {MONTHS.map((_, i) => (
                <React.Fragment key={i}>
                  <th style={{ ...mono(8, "#94a3b8", 700), padding: "5px 5px", textAlign: "center" }}>Rev</th>
                  <th style={{ ...mono(8, "#94a3b8", 700), padding: "5px 5px", textAlign: "center" }}>Count</th>
                  <th style={{ ...mono(8, "#94a3b8", 700), padding: "5px 5px", textAlign: "center" }}>Margin</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={row.id || "all"} data-testid={row.testid} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <td style={{ padding: "6px 8px", position: "sticky", left: 0, background: ri === 0 ? "rgba(24,95,165,0.18)" : "rgba(0,0,0,0.20)", ...mono(11, undefined, 700) }}>
                  {ri === 0 ? <Users size={11} className="inline mr-1" /> : null}
                  {row.label}
                </td>
                {Array.from({ length: 12 }, (_, mi) => {
                  const m = mi + 1;
                  const k = _cellKey(row.tenant_id ?? row.id, m);
                  const c = draft[k] || _emptyCell();
                  return (
                    <React.Fragment key={mi}>
                      <td style={{ padding: "3px 3px" }}>
                        {cellInput(c.planned_revenue, (v) => set(k, "planned_revenue", v), `${row.testid}-m${m}-revenue`)}
                      </td>
                      <td style={{ padding: "3px 3px" }}>
                        {cellInput(c.planned_freight_count, (v) => set(k, "planned_freight_count", v), `${row.testid}-m${m}-count`)}
                      </td>
                      <td style={{ padding: "3px 3px" }}>
                        {cellInput(c.planned_margin, (v) => set(k, "planned_margin", v), `${row.testid}-m${m}-margin`)}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3" style={mono(10, "#94a3b8")}>
        Tip: leave a cell blank to clear it. Plans you save here drive the <b>variance pills</b> on the BI dashboard.
      </div>
    </div>
  );
}
